import { File as FileIcon, FileText, Loader2, Mic, Paperclip, Trash2, Upload } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { formatError } from "@/auth";
import { HoverPreview, Lightbox, fileUrl, kindOf } from "@/components/pipeline/AttachmentViewer";

const Thumb = ({ leadId, a }) => {
  const kind = kindOf(a);
  const [broken, setBroken] = useState(false);
  if (kind === "image") return <img src={fileUrl(leadId, a)} alt="" loading="lazy" />;
  if (kind === "pdf" && !broken) return <img className="pl-att-pdfthumb" src={fileUrl(leadId, a).replace(/\/file$/, "/thumb")} alt="" loading="lazy" onError={() => setBroken(true)} />;
  return <span className={`pl-att-icon ${kind}`}>{kind === "pdf" ? <FileText size={26} /> : kind === "audio" ? <Mic size={26} /> : <FileIcon size={26} />}<em>{(a.name.split(".").pop() || "").slice(0, 4).toUpperCase()}</em></span>;
};

export function LeadAttachments({ leadId }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(0);
  const [hover, setHover] = useState(null);
  const [open, setOpen] = useState(null);
  const [drag, setDrag] = useState(false);
  const input = useRef(null);
  const timer = useRef(null);

  const load = useCallback(() => leadId && api.get(`/pipeline/leads/${leadId}/attachments`).then(({ data }) => setItems(data)).catch((e) => { setItems([]); toast.error(formatError(e.response?.data?.detail)); }), [leadId]);
  useEffect(() => { setItems(null); load(); }, [load]);

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(list.length);
    let ok = 0;
    for (const f of list) {
      const body = new FormData();
      body.append("file", f);
      try { await api.post(`/pipeline/leads/${leadId}/attachments`, body, { timeout: 180000 }); ok += 1; } catch (e) { toast.error(`${f.name} : ${formatError(e.response?.data?.detail)}`); }
      setBusy((n) => n - 1);
    }
    if (ok) toast.success(ok > 1 ? `${ok} pièces jointes ajoutées dans noCRM.` : "Pièce jointe ajoutée dans noCRM.");
    load();
  };
  const remove = async (a) => {
    if (!window.confirm(`Supprimer « ${a.name} » ? Elle sera aussi supprimée dans noCRM.`)) return;
    clearTimeout(timer.current); setHover(null);
    try { await api.delete(`/pipeline/leads/${leadId}/attachments/${a.id}`); setItems((xs) => xs.filter((x) => x.id !== a.id)); toast.success("Pièce jointe supprimée."); } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const enter = (a, e) => { const r = e.currentTarget.getBoundingClientRect(); const side = r.left + r.width / 2 < window.innerWidth / 2 ? "right" : "left"; clearTimeout(timer.current); timer.current = setTimeout(() => setHover({ a, side }), 350); };
  const leave = () => { clearTimeout(timer.current); setHover(null); };

  if (!leadId) return null;
  return (
    <section className={`pl-sheet-block pl-att${drag ? " drag" : ""}`} onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }} data-testid="lead-attachments">
      <div className="pl-block-head">
        <h3><Paperclip size={14} /> Pièces jointes <small className="pl-muted">{items ? items.length : "…"}</small></h3>
        <button type="button" className="pl-toggle" onClick={() => input.current?.click()} disabled={busy > 0} data-testid="attachment-add-btn">{busy > 0 ? <><Loader2 size={14} className="spin" /> Envoi ({busy})…</> : <><Upload size={14} /> Ajouter</>}</button>
        <input ref={input} type="file" multiple hidden onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="attachment-input" />
      </div>
      {items?.length === 0 && <p className="pl-muted pl-att-empty" data-testid="attachments-empty">Glissez des fichiers ici ou cliquez sur Ajouter.</p>}
      {items?.length > 0 && (
        <div className="pl-att-strip" data-testid="attachments-strip">
          {items.map((a, i) => (
            <figure key={a.id} className="pl-att-item" onMouseEnter={(e) => enter(a, e)} onMouseLeave={leave} data-testid={`attachment-${a.id}`}>
              <button type="button" className="pl-att-thumb" onClick={() => { leave(); setOpen(i); }} data-testid={`attachment-open-${a.id}`}><Thumb leadId={leadId} a={a} /></button>
              <figcaption title={a.name}>{a.name}</figcaption>
              <button type="button" className="pl-att-del" onClick={() => remove(a)} aria-label="Supprimer" data-testid={`attachment-delete-${a.id}`}><Trash2 size={12} /></button>
            </figure>
          ))}
        </div>
      )}
      {hover && open === null && <HoverPreview leadId={leadId} a={hover.a} side={hover.side} />}
      {open !== null && <Lightbox leadId={leadId} items={items} index={open} setIndex={setOpen} onClose={() => setOpen(null)} />}
    </section>
  );
}
